import { Injectable, inject } from '@angular/core';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationService, NotificationServiceInjector } from '@next/core-lib/notification';
import { UserSelectors } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, race, timer } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
  toArray,
  withLatestFrom,
} from 'rxjs/operators';

import { AppState, ExternalCropFieldSources } from 'src/app/shared/models';

import { ProtectedFieldsSkippedDialogComponent } from '../../components/protected-fields-skipped-dialog/protected-fields-skipped-dialog.component';
import {
  CropFieldEvents,
  FmsCropFieldSyncProtectedFieldsSkippedEvent,
  ProtectedFieldsSkippedGroup,
} from '../../models';
import {
  CompleteSyncCropFieldsV2Request,
  CropFieldActionTypes,
} from '../actions/crop-field.actions';

const BMS_SOURCES: ReadonlySet<ExternalCropFieldSources> = new Set([
  ExternalCropFieldSources.Dacom,
  ExternalCropFieldSources.CropVision,
]);

// Hard cap for a single BMS sync window. Beyond this we close the buffer even
// if no LoadCropFieldsSuccess arrives, so a stalled sync can't trap events forever.
const SYNC_WINDOW_FALLBACK_MS = 60_000;

// Backend SignalR events frequently arrive *after* the cropfields reload has
// resolved. We hold the window open for a short grace period after reload to
// catch those late stragglers in the same dialog.
const POST_RELOAD_GRACE_MS = 1500;

@Injectable()
export class ProtectedFieldsSkippedNotificationEffects {
  private readonly notification = inject<NotificationService>(NotificationServiceInjector);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly actions$ = inject(Actions);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);

  private readonly protectedFieldsSkipped$: Observable<FmsCropFieldSyncProtectedFieldsSkippedEvent> =
    this.notification
      .on<
        FmsCropFieldSyncProtectedFieldsSkippedEvent | string
      >(CropFieldEvents.fmsCropFieldSyncProtectedFieldsSkipped)
      .pipe(
        map((event) => this.parseEvent(event)),
        filter((event): event is FmsCropFieldSyncProtectedFieldsSkippedEvent => !!event),
        withLatestFrom(this.store.select(UserSelectors.selectCustomer)),
        filter(([event, customer]) => customer?.id === event.CustomerId),
        map(([event]) => event),
      );

  showProtectedFieldsSkippedDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<CompleteSyncCropFieldsV2Request>(
          CropFieldActionTypes.CompleteSyncCropFieldsV2Request,
        ),
        filter((action) => BMS_SOURCES.has(action.externalCropFieldSource)),
        switchMap(() => {
          const windowClose$ = race(
            this.actions$.pipe(
              ofType(CropFieldActionTypes.LoadCropFieldsSuccess),
              take(1),
              concatMap(() => timer(POST_RELOAD_GRACE_MS)),
            ),
            timer(SYNC_WINDOW_FALLBACK_MS),
          );

          return this.protectedFieldsSkipped$.pipe(
            takeUntil(windowClose$),
            toArray(),
            filter((events) => events.length > 0),
            map((events) => this.groupByCropField(events)),
            concatMap((groups) =>
              this.dialog
                .dialog(
                  ProtectedFieldsSkippedDialogComponent,
                  {
                    title: this.locale.translate('protectedFieldsSkippedDialog.title'),
                    groups,
                    confirmButton: true,
                    cancelButton: false,
                    confirmButtonText: this.locale.translate('ok'),
                  },
                  { maxWidth: 800 },
                )
                .pipe(catchError(() => EMPTY)),
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  private parseEvent(
    event: FmsCropFieldSyncProtectedFieldsSkippedEvent | string,
  ): FmsCropFieldSyncProtectedFieldsSkippedEvent | null {
    const root = this.parseRaw(event);
    if (!root) {
      return null;
    }

    const payload = this.unwrapEnvelope(root);
    if (!payload) {
      return null;
    }

    const customerId = this.readNumber(payload, 'CustomerId', 'customerId');
    const cropFieldId = this.readNumber(payload, 'CropFieldId', 'cropFieldId');
    const cropFieldName = this.readString(payload, 'CropFieldName', 'cropFieldName');
    const skippedRaw = this.readValue(payload, 'SkippedFields', 'skippedFields');

    if (
      customerId === null ||
      cropFieldId === null ||
      !cropFieldName ||
      !Array.isArray(skippedRaw)
    ) {
      return null;
    }

    return {
      CustomerId: customerId,
      CropFieldId: cropFieldId,
      CropFieldName: cropFieldName,
      SkippedFields: skippedRaw.filter((field): field is string => typeof field === 'string'),
    };
  }

  private parseRaw(
    event: FmsCropFieldSyncProtectedFieldsSkippedEvent | string,
  ): Record<string, unknown> | null {
    if (typeof event !== 'string') {
      return event as unknown as Record<string, unknown>;
    }
    try {
      const parsed = JSON.parse(event) as unknown;
      return this.isRecord(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  private unwrapEnvelope(event: Record<string, unknown>): Record<string, unknown> | null {
    const inner = this.readValue(event, 'data', 'Data');
    if (typeof inner === 'string') {
      try {
        const parsed = JSON.parse(inner) as unknown;
        return this.isRecord(parsed) ? parsed : null;
      } catch {
        return null;
      }
    }
    if (this.isRecord(inner)) {
      return inner;
    }
    return event;
  }

  private readValue(event: Record<string, unknown>, ...keys: string[]): unknown {
    for (const key of keys) {
      if (key in event) {
        return event[key];
      }
    }
    return undefined;
  }

  private readString(event: Record<string, unknown>, ...keys: string[]): string | null {
    const value = this.readValue(event, ...keys);
    return typeof value === 'string' && value.length > 0 ? value : null;
  }

  private readNumber(event: Record<string, unknown>, ...keys: string[]): number | null {
    const value = this.readValue(event, ...keys);
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private groupByCropField(
    events: FmsCropFieldSyncProtectedFieldsSkippedEvent[],
  ): ProtectedFieldsSkippedGroup[] {
    // Key by CropFieldId, not name: two crop fields can share a display name.
    const groupsById = new Map<number, ProtectedFieldsSkippedGroup>();

    for (const event of events) {
      const existing = groupsById.get(event.CropFieldId);
      if (existing) {
        for (const field of event.SkippedFields) {
          if (!existing.skippedFields.includes(field)) {
            existing.skippedFields.push(field);
          }
        }
        continue;
      }

      groupsById.set(event.CropFieldId, {
        cropFieldId: event.CropFieldId,
        cropFieldName: event.CropFieldName,
        skippedFields: [...event.SkippedFields],
      });
    }

    return Array.from(groupsById.values());
  }
}
